// src/App.js

import React, { useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { initializePayrollService } from './services/payrollService';
import { AuthProvider } from './context/AuthContext';
import { TenantProvider } from './context/TenantContext';


import Layout from './components/common/Layout';
import AuthGuard from './components/auth/AuthGuard';
import IdleWarningModal from './components/auth/IdleWarningModal';
import ReAuthWrapper from './components/common/ReAuthWrapper';

import Home from './pages/Home';
import Calendar from './pages/Calendar';
import Clients from './pages/Clients';
import RoomAllocation from './pages/RoomAllocation';
import Admin from './pages/Admin';
import Settings from './pages/Settings';
import WaitlistView from './components/WaitlistView/WaitlistView';
import FirebaseTest from './pages/FirebaseTest';
import TenantSetup from './pages/TenantSetup';
import AdminSetup from './pages/AdminSetup';
import ForceRefresh from './pages/ForceRefresh';
import SuperAdminUpgrade from './pages/SuperAdminUpgrade';
import SuperAdmin from './pages/SuperAdmin';
import CleanInitialization from './components/setup/CleanInitialization';
import EmailTest from './pages/EmailTest';
import RestoreSuperAdmin from './pages/RestoreSuperAdmin';
import UpdateProfile from './pages/UpdateProfile';
import DevSetup from './pages/DevSetup';
import TempSuperAdminSetup from './pages/TempSuperAdminSetup';
import CleanupFirestore from './pages/CleanupFirestore';
import AuthActionHandler from './components/auth/AuthActionHandler';
import Welcome from './pages/Welcome';
import ClientFeedback from './pages/ClientFeedback';
import PublicBookingFlow from './components/booking/PublicBookingFlow';

import { createUserProfileForExistingUser } from './utils/createUserProfile';
import { debugAuthentication } from './utils/debugAuth';


function App() {
  useEffect(() => {
    // Initialize payroll automation service
    initializePayrollService();

    // Make user profile creation available in browser console
    window.createUserProfile = () => createUserProfileForExistingUser('christheale@gmail.com', 'Matrix=12');

    // Make authentication debugging available in browser console
    window.debugAuth = debugAuthentication;
  }, []);

  return (
    <AuthProvider>
      <TenantProvider>
        <ReAuthWrapper>
          <Toaster position="top-center" />
          <IdleWarningModal />
          <Routes>
            {/* Firebase Auth Action Handler - Must be outside AuthGuard */}
            <Route path="/__/auth/action" element={<AuthActionHandler />} />
            {/* Welcome page for password setup completion */}
            <Route path="/welcome" element={<Welcome />} />
            {/* Client feedback page - public access, no auth required */}
            <Route path="/feedback/:invoiceNumber" element={<ClientFeedback />} />
            {/* Public booking flow - no auth required */}
            <Route path="/book/:tenantSlug" element={<PublicBookingFlow />} />
            <Route element={
              <AuthGuard>
                <Layout />
              </AuthGuard>
            }>
              <Route path="/" element={<Home />} />
              <Route path="/calendar" element={<Calendar />} />
              <Route path="/clients/*" element={<Clients />} />
              <Route path="/rooms" element={<RoomAllocation />} />
              <Route path="/waitlist" element={<WaitlistView />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/admin/*" element={<Admin />} />
              <Route path="/super-admin" element={<SuperAdmin />} />
              <Route path="/firebase-test" element={<FirebaseTest />} />
              <Route path="/tenant-setup" element={<TenantSetup />} />
              <Route path="/admin-setup" element={<AdminSetup />} />
              <Route path="/force-refresh" element={<ForceRefresh />} />
              <Route path="/super-admin-upgrade" element={<SuperAdminUpgrade />} />
              <Route path="/clean-init" element={<CleanInitialization />} />
              <Route path="/email-test" element={<EmailTest />} />
              <Route path="/restore-super-admin" element={<RestoreSuperAdmin />} />
              <Route path="/update-profile" element={<UpdateProfile />} />
              <Route path="/dev-setup" element={<DevSetup />} />
              <Route path="/temp-super-admin" element={<TempSuperAdminSetup />} />
              <Route path="/cleanup-firestore" element={<CleanupFirestore />} />
            </Route>
          </Routes>
        </ReAuthWrapper>
      </TenantProvider>
    </AuthProvider>
  );
}

export default App;